import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, doc as docRef, getDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import {
  antesDeSubir, agruparPorClave, esClaveValida, permisosDeLaBiblioteca,
  MENSAJES, AVISOS, MAX_FOTOS_POR_GRUPO,
} from '../../utils/limitesDeBiblioteca';
import './BibliotecaDeMedios.css';

/**
 * LA BIBLIOTECA DE MEDIOS DEL BOT (C4, 28-oct-2026): los archivos que el bot puede ENVIAR al cliente
 * (PDF de cotización, grupos de 1 a 10 fotos, notas de voz OGG). No son el Repositorio de arriba —ése
 * se lee para responder—; éstos salen tal cual por WhatsApp con la tool `enviarArchivo`.
 *
 * LA SUBIDA, en tres pasos y EN SERIE (una foto detrás de otra, para que el orden del grupo sea el
 * de la selección):
 *   1. `pedirSubidaABiblioteca` → URL firmada para UN objeto y las cabeceras que hay que mandar;
 *   2. PUT directo a Storage con ESAS cabeceras (están firmadas: si falta una, Storage da 403);
 *   3. `finalizarSubidaABiblioteca` → el servidor valida los bytes y crea el documento.
 * Antes de pedir nada, `antesDeSubir` (compartido con el servidor) bloquea lo que seguro se rechaza
 * y enseña los avisos no bloqueantes (nota de voz > 512 KB; ≥ 10 MB, descarga lenta).
 *
 * La lista se lee en vivo de `bots/{botId}/mediaLibrary` (regla: canViewBots). Subir y borrar sólo
 * owner/admin (`canManageIntegrations`); el resto ve la lista.
 *
 * Sin vista previa del archivo: es lo único que se recortó para llegar al 5-nov (decisión de Rodrigo).
 *
 * LOS CAMPOS SON GENÉRICOS (29-oct, decisión de Rodrigo): clave, categoría, subcategoría y
 * descripción, para cualquier negocio. Etiqueta ENCIMA de cada campo y su ayuda debajo, atada con
 * `aria-describedby`; dos columnas en escritorio y una en móvil (`BibliotecaDeMedios.css`).
 */
export default function BibliotecaDeMedios({ botId, organizationId, context }) {
  const { t } = useTranslation('bots');
  const permisos = permisosDeLaBiblioteca(context);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [subiendo, setSubiendo] = useState(null);       // { hecho, total } mientras sube
  const [archivos, setArchivos] = useState([]);
  const [clave, setClave] = useState('');
  const [categoria, setCategoria] = useState('');
  const [subcategoria, setSubcategoria] = useState('');
  const [caption, setCaption] = useState('');
  const id = useId();   // ids únicos para atar cada etiqueta y cada ayuda a su campo
  // Los PDF que nombran las reglas de cotización: la casilla «Es una cotización» sale marcada por defecto en ellos
  // (3-nov-2026, F; el mismo criterio que `functions/bot/media/archivoDeCotizacion.js`).
  const [pdfsDeLasReglas, setPdfsDeLasReglas] = useState(() => new Set());
  const [reglasLeidas, setReglasLeidas] = useState(false);   // sin leerlas, no se promete «automático»
  const [marcando, setMarcando] = useState(null);

  const base = organizationId ? ['organizations', organizationId] : ['clientes', auth.currentUser?.uid];

  useEffect(() => {
    if (!botId || !permisos.ver || !base[1]) return undefined;
    const ref = collection(db, ...base, 'bots', botId, 'mediaLibrary');
    return onSnapshot(ref,
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => { console.error('[BibliotecaDeMedios] onSnapshot', e); setError(t('biblioteca.errorLista')); });
    // `base` se deriva de organizationId y del usuario: con eso basta como dependencia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId, organizationId, permisos.ver]);

  useEffect(() => {
    if (!botId || !permisos.ver || !base[1]) return;
    getDoc(docRef(db, ...base, 'bots', botId, 'cotizacion', 'config'))
      .then((s) => {
        const reglas = s.exists() && Array.isArray(s.data()?.reglas) ? s.data().reglas : [];
        setPdfsDeLasReglas(new Set(reglas.map((r) => r?.pdf).filter((k) => typeof k === 'string' && k)));
        setReglasLeidas(true);
      })
      .catch(() => { setPdfsDeLasReglas(new Set()); setReglasLeidas(false); });   // sin reglas legibles, sólo lo marcado a mano
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId, organizationId, permisos.ver]);

  // La marca de un archivo: la de la casilla si alguien la tocó; si no, «automático» según las reglas.
  const marcaDe = (g) => {
    const tocada = docs.find((d) => d.clave === g.clave && typeof d.esCotizacion === 'boolean');
    return tocada
      ? { esCotizacion: tocada.esCotizacion, automatica: false }
      : { esCotizacion: g.tipo === 'pdf' && pdfsDeLasReglas.has(g.clave), automatica: true };
  };
  const marcar = async (clave, esCotizacion) => {
    setError(null);
    setMarcando(clave);
    try {
      await httpsCallable(getFunctions(), 'marcarCotizacionEnBiblioteca')({ organizationId, botId, clave, esCotizacion });
    } catch (e) {
      console.error('[BibliotecaDeMedios] marcarCotizacionEnBiblioteca', e);
      setError(t('biblioteca.errorMarcar'));   // el mensaje crudo del servidor no es para el operador
    } finally {
      setMarcando(null);
    }
  };

  // Lo que se sabe de la selección antes de subir: el tipo, si alguno se bloquea y los avisos.
  const vistos = archivos.map((f) => ({ archivo: f, ...antesDeSubir(f) }));
  const tipos = [...new Set(vistos.map((v) => v.tipo).filter(Boolean))];
  const bloqueado = vistos.find((v) => v.bloquea);
  const mezcla = tipos.length > 1;
  const variosNoFotos = vistos.length > 1 && tipos[0] !== 'imagen';
  const demasiadasFotos = tipos[0] === 'imagen' && vistos.length > MAX_FOTOS_POR_GRUPO;
  const avisosPrevios = [...new Set(vistos.flatMap((v) => v.avisos))];
  const claveOk = esClaveValida(clave);
  const puedeSubir = permisos.gestionar && vistos.length > 0 && !bloqueado && !mezcla && !variosNoFotos && !demasiadasFotos && claveOk && !subiendo;

  const subir = async () => {
    setError(null);
    setAvisos([]);
    const functions = getFunctions();
    const pedir = httpsCallable(functions, 'pedirSubidaABiblioteca');
    // 130 s: por encima de los 120 del callable, que ahora convierte (por defecto el cliente corta a los 70 s y
    // enseñaría un error con el documento creándose).
    const finalizar = httpsCallable(functions, 'finalizarSubidaABiblioteca', { timeout: 130 * 1000 });
    const recibidos = [];
    try {
      for (let i = 0; i < vistos.length; i++) {
        setSubiendo({ hecho: i, total: vistos.length });
        const { archivo, tipo, mime } = vistos[i];
        const { data: pedido } = await pedir({
          organizationId, botId, clave, tipo, mime, bytes: archivo.size,
          nombreArchivo: archivo.name, caption: caption || null, categoria: categoria || null, subcategoria: subcategoria || null,
        });
        const put = await fetch(pedido.url, { method: pedido.metodo, headers: pedido.cabeceras, body: archivo });
        if (!put.ok) throw new Error(t('biblioteca.errorSubida', { status: put.status }));
        const { data: final } = await finalizar({ organizationId, botId, idSubida: pedido.idSubida });
        recibidos.push(...(final.avisos || []).map((a) => a.mensaje));
      }
      setAvisos([...new Set(recibidos)]);
      setArchivos([]);
      setClave(''); setCategoria(''); setSubcategoria(''); setCaption('');
    } catch (e) {
      // El motivo del servidor viene en `details.motivo`; su texto, en el mismo idioma que el aviso previo.
      const motivo = e?.details?.motivo;
      setError((motivo && MENSAJES[motivo]) || e?.message || t('biblioteca.errorDesconocido'));
    } finally {
      setSubiendo(null);
    }
  };

  const borrar = async (id) => {
    setError(null);
    try {
      await httpsCallable(getFunctions(), 'borrarArchivoDeBiblioteca')({ organizationId, botId, id });
    } catch (e) {
      setError(e?.message || t('biblioteca.errorDesconocido'));
    }
  };

  if (!permisos.ver) return null;
  const grupos = agruparPorClave(docs);
  const kb = (b) => (b >= 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <div className="knowledge-documents" style={{ marginTop: '2rem' }}>
      <h3>{t('biblioteca.titulo')}</h3>
      <p className="upload-hint">{t('biblioteca.descripcion')}</p>

      {error && <div className="message message-error">{error}</div>}
      {avisos.map((a) => <div key={a} className="message message-warning">{a}</div>)}

      {permisos.gestionar ? (
        <div className="upload-area" style={{ textAlign: 'left' }}>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.ogg,.opus,.m4a,.mp3,.wav"
            multiple
            disabled={!!subiendo}
            onChange={(e) => setArchivos([...(e.target.files || [])])}
          />
          <p className="upload-hint">{t('biblioteca.formatos', { fotos: MAX_FOTOS_POR_GRUPO })}</p>

          {bloqueado && <div className="message message-error">{bloqueado.archivo.name}: {MENSAJES[bloqueado.bloquea]}</div>}
          {mezcla && <div className="message message-error">{t('biblioteca.mezcla')}</div>}
          {variosNoFotos && <div className="message message-error">{t('biblioteca.variosNoFotos')}</div>}
          {demasiadasFotos && <div className="message message-error">{t('biblioteca.demasiadasFotos', { fotos: MAX_FOTOS_POR_GRUPO })}</div>}
          {avisosPrevios.map((a) => <div key={a} className="message message-warning">{AVISOS[a]}</div>)}

          <div className="biblioteca-campos">
            <div className="biblioteca-campo biblioteca-campo-ancho">
              <label htmlFor={`${id}-clave`}>{t('biblioteca.clave')}</label>
              <input
                id={`${id}-clave`} value={clave} onChange={(e) => setClave(e.target.value.trim())} required
                placeholder={t('biblioteca.clavePlaceholder')} autoComplete="off" spellCheck={false}
                aria-describedby={`${id}-clave-ayuda${clave && !claveOk ? ` ${id}-clave-error` : ''}`} aria-invalid={!!clave && !claveOk}
              />
              <p id={`${id}-clave-ayuda`} className="biblioteca-ayuda">{t('biblioteca.claveAyuda')}</p>
              {clave && !claveOk && <div id={`${id}-clave-error`} className="message message-error">{MENSAJES['clave-invalida']}</div>}
            </div>
            {/* Categoría y subcategoría comparten UNA ayuda, bajo el par y atada a los dos campos
                (decisión de Rodrigo, 29-oct). En móvil, una columna: queda bajo la subcategoría. */}
            <div className="biblioteca-campo biblioteca-par biblioteca-par-primero">
              <label htmlFor={`${id}-categoria`}>{t('biblioteca.categoria')}</label>
              <input
                id={`${id}-categoria`} value={categoria} onChange={(e) => setCategoria(e.target.value)} maxLength={60}
                placeholder={t('biblioteca.categoriaPlaceholder')} aria-describedby={`${id}-categorias-ayuda`}
              />
            </div>
            <div className="biblioteca-campo biblioteca-par">
              <label htmlFor={`${id}-subcategoria`}>{t('biblioteca.subcategoria')}</label>
              <input
                id={`${id}-subcategoria`} value={subcategoria} onChange={(e) => setSubcategoria(e.target.value)} maxLength={60}
                placeholder={t('biblioteca.subcategoriaPlaceholder')} aria-describedby={`${id}-categorias-ayuda`}
              />
            </div>
            <p id={`${id}-categorias-ayuda`} className="biblioteca-ayuda biblioteca-ayuda-par">{t('biblioteca.categoriaAyuda')}</p>
            <div className="biblioteca-campo biblioteca-campo-ancho">
              <label htmlFor={`${id}-caption`}>{t('biblioteca.caption')}</label>
              <textarea
                id={`${id}-caption`} value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={1024} rows={2}
                placeholder={t('biblioteca.captionPlaceholder')} aria-describedby={`${id}-caption-ayuda`}
              />
              <p id={`${id}-caption-ayuda`} className="biblioteca-ayuda">{t('biblioteca.captionAyuda')}</p>
            </div>
          </div>

          <button type="button" className="btn-primary" disabled={!puedeSubir} onClick={subir}>
            {subiendo ? t('biblioteca.subiendo', { hecho: subiendo.hecho + 1, total: subiendo.total }) : t('biblioteca.subir')}
          </button>
        </div>
      ) : (
        <p className="upload-hint">{t('biblioteca.sinPermiso')}</p>
      )}

      <div className="documents-list">
        <h4>{t('biblioteca.lista', { count: grupos.length })}</h4>
        {grupos.length === 0 ? (
          <div className="empty-state"><p>{t('biblioteca.vacia')}</p></div>
        ) : (
          <div className="documents-table">
            {grupos.map((g) => (
              <div key={g.clave} className="document-row">
                <div>
                  <strong>{g.clave}</strong> · {t(`biblioteca.tipos.${g.tipo}`)}
                  {g.tipo === 'imagen' && ` · ${t('biblioteca.fotos', { count: g.archivos.length, max: MAX_FOTOS_POR_GRUPO })}`}
                  {g.categoria && ` · ${g.categoria}`}{g.subcategoria && ` · ${g.subcategoria}`} · {kb(g.bytes)}
                  {g.caption && <div className="upload-hint">{g.caption}</div>}
                  {g.avisos.map((a) => <div key={a} className="upload-hint">⚠️ {AVISOS[a]}</div>)}
                  <label className="upload-hint" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <input
                      type="checkbox"
                      checked={marcaDe(g).esCotizacion}
                      disabled={!permisos.gestionar || marcando === g.clave}
                      onChange={(e) => marcar(g.clave, e.target.checked)}
                    />
                    {t('biblioteca.esCotizacion')}{marcaDe(g).automatica && reglasLeidas && ` ${t('biblioteca.esCotizacionAuto')}`}
                    {!marcaDe(g).automatica && permisos.gestionar && (
                      <button type="button" className="btn-link" disabled={marcando === g.clave} onClick={() => marcar(g.clave, null)}>
                        {t('biblioteca.esCotizacionRestablecer')}
                      </button>
                    )}
                  </label>
                </div>
                {permisos.gestionar && (
                  <div>
                    {g.archivos.map((a) => (
                      <button key={a.id} type="button" className="btn-delete" onClick={() => borrar(a.id)} disabled={!!subiendo}>
                        {g.tipo === 'imagen' ? t('biblioteca.borrarFoto', { orden: a.orden }) : t('biblioteca.borrar')}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
